/**
 * Chunk-load-safe replacement for `React.lazy`.
 *
 * ## The failure this exists to stop
 *
 * Every route in `src/routes/` is code-split, so navigating to a page fetches a hashed chunk
 * (`/assets/ExitDocumentTemplates-a1b2c3.js`). Two things make that fetch fragile in production:
 *
 * 1. `nginx.conf` serves the SPA with `try_files $uri $uri/ /index.html`. A chunk name that no
 *    longer exists - which is every chunk of the previous build, the moment a new build is
 *    deployed - therefore comes back as **index.html with 200 OK and `Content-Type: text/html`**,
 *    not as a 404. The browser refuses to execute HTML as an ES module and the dynamic import
 *    rejects with "Failed to fetch dynamically imported module" / "Expected a JavaScript module
 *    script but the server responded with a MIME type of text/html".
 * 2. `React.lazy` **caches the rejection permanently**. Once a lazy component's promise rejects,
 *    every later render of it re-throws the same error; nothing short of re-evaluating the module
 *    graph clears it.
 *
 * Together those produce the exact symptom QA reported: the page throws to the ErrorBoundary
 * ("Something went wrong") or leaves a Suspense fallback on screen, and **only a manual browser
 * refresh fixes it** - because a refresh fetches a fresh `index.html`, which names the chunks of
 * the build that is actually deployed.
 *
 * ## What this does about it
 *
 * - Retries the import a couple of times with a short backoff, which clears a genuinely transient
 *   network blip without any visible failure.
 * - If it still fails and the error looks like a chunk-load error, performs the page reload the
 *   user would otherwise have had to perform by hand - **once**, guarded by a sessionStorage
 *   timestamp so a permanently broken deploy cannot put the browser in a reload loop.
 * - If the reload is refused (storage blocked) or has already been spent, the original error is
 *   re-thrown so the ErrorBoundary shows its recovery UI rather than the app hanging.
 *
 * Non-chunk errors (a genuine bug in the module's top level) are re-thrown immediately and
 * untouched - retrying or reloading those would only hide a real defect.
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/** How many extra attempts to make before giving up on the fetch. */
const RETRIES = 2;

/** Backoff between attempts; multiplied by the attempt number. */
const RETRY_DELAY_MS = 350;

const RELOAD_KEY = 'hrms.chunkReloadAt';

/**
 * How long a spent reload stays spent. Long enough that a broken deploy cannot loop, short enough
 * that a user who hits a second, unrelated stale chunk an hour later still gets an automatic
 * recovery instead of an error screen.
 */
const RELOAD_WINDOW_MS = 60_000;

/**
 * Does this error mean "the JS file could not be loaded" rather than "the module threw"?
 *
 * The message text differs per browser and there is no single reliable error code, so this matches
 * every wording currently emitted by Chrome, Firefox and Safari, plus webpack-era `ChunkLoadError`
 * for anything still throwing it.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  const err = error as { name?: string; message?: string } | null | undefined;
  const name = String(err?.name ?? '');
  const message = String(err?.message ?? error ?? '');

  if (name === 'ChunkLoadError') return true;

  return (
    // Chrome / Edge
    /Failed to fetch dynamically imported module/i.test(message) ||
    // The MIME-type refusal produced by the nginx SPA fallback described above
    /expected a javascript(-or-wasm)? module script/i.test(message) ||
    // Firefox
    /error loading dynamically imported module/i.test(message) ||
    // Safari
    /Importing a module script failed/i.test(message) ||
    // Older bundler wording, and CSS chunks
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk/i.test(message)
  );
};

/**
 * Reload the page at most once per {@link RELOAD_WINDOW_MS}.
 *
 * Returns true when a reload was actually started, so the caller knows the page is going away and
 * can stop trying to render an error frame that would flash for a few milliseconds and vanish.
 *
 * Storage access is wrapped because `sessionStorage` throws outright in some privacy modes and in
 * sandboxed iframes. If we cannot record that a reload was spent we cannot guarantee we will not
 * loop, so in that case we decline to reload at all and let the error surface instead.
 */
const reloadOncePerWindow = (): boolean => {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Number.isFinite(lastReloadAt) && Date.now() - lastReloadAt < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }

  window.location.reload();
  return true;
};

/**
 * Reload once if `error` is a chunk-load failure, honouring the same one-per-window budget.
 *
 * For chunk errors that arrive somewhere `lazyRetry` cannot wrap them - the tab panes inside the
 * workspace pages still call `React.lazy` directly, and an ErrorBoundary is where their failure
 * surfaces. Returns true when a reload was started.
 */
export const recoverFromChunkError = (error: unknown): boolean =>
  isChunkLoadError(error) && reloadOncePerWindow();

/**
 * Run a dynamic import with the retry-then-reload policy described above.
 *
 * Exported on its own because `main.tsx` needs it for `import('./App')`, which is not a lazy
 * component but fails in exactly the same way - and fails worse, because a rejection there means
 * `createRoot(...).render()` is never reached and the page stays blank forever.
 */
export const importWithRetry = async <T>(factory: () => Promise<T>): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= RETRIES; attempt += 1) {
    try {
      return await factory();
    } catch (error) {
      lastError = error;
      // A real error inside the module is not something a retry or a reload can fix.
      if (!isChunkLoadError(error)) throw error;
      if (attempt < RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS * (attempt + 1)));
      }
    }
  }

  if (reloadOncePerWindow()) {
    // The document is unloading. Stay pending briefly so React does not paint the error screen
    // during the teardown, but do not stay pending forever: if the reload is somehow blocked, the
    // throw below still runs and the user gets a recovery UI rather than a permanent spinner.
    await new Promise((resolve) => setTimeout(resolve, 10_000));
  }

  throw lastError;
};

/**
 * Drop-in replacement for `React.lazy`.
 *
 * @example
 *   const Payslips = lazyRetry(() => import('../../pages/HrPayroll/Payslips'));
 */
export const lazyRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> => lazy(() => importWithRetry(factory));

export default lazyRetry;
